import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useAccount } from './../../../contexts/Account';
import {
  Dialog,
  Slide,
  useMediaQuery,
  Button,
  Typography,
  Box,
  List,
  ListItem,
} from '@mui/material';
import { Clear } from '@mui/icons-material';
import FreemiumStep1 from './FreemiumStep1';
import FreemiumStep2 from './FreemiumStep2';
import eventBus, { eventBusValues } from './../../../eventBus';
import { getCompanyRecurlyPlan } from './../../../services/company_services';
import { getAdvisorPlanUpgrades } from './../../../services/advisor_services';
import AdvisoryCloudPlusOnboarding from '../../../hooks/AdvisoryCloudPlusOnboarding';
import { sendFSEventUpgradeViewed } from '../../../services/FullStory';

const CompanyFreemiumModal = () => {
  const {
    accountDetails,
    upgradeFreemiumAccount,
    currentPlan,
    type,
    currentUserInfo,
    asLoggedUserDetails,
  } = useAccount();
  const location = useLocation();

  const isSM = useMediaQuery((theme) => theme.breakpoints.down('sm'));
  const [openModal, setOpenModal] = useState(false);
  const [step1, setStep1] = useState(true);
  const [step2, setStep2] = useState(false);
  const [step3, setStep3] = useState(false);
  const [isFirstLoad, setIsFirstLoad] = useState(true);
  const [monthlyUpgradePlus, setMonthlyUpgradePlus] = useState('');
  const [monthlyUpgradeManaged, setMonthlyUpgradeManaged] = useState('');
  const [annualUpgradePlus, setAnnualUpgradePlus] = useState(''); // acPlusUpgradeOptionTwo
  const [annualUpgradeManaged, setAnnualUpgradeManaged] = useState(''); // freemiumUpgradeTwo
  const [planData, setPlanData] = useState({ plan: 'mo', option: 1, data: '' });
  const [hideBack, setHideBack] = useState(false);
  const [pendingAction, setPendingAction] = useState(null);
  const [selectedPlan, setSelectedPlan] = useState('');
  const [isUpgrading, setIsUpgrading] = useState(false);

  const handleContinue = () => {
    setStep1(false);
  };

  const handleContinueStep3 = () => {
    setStep1(false);
    setStep2(true);
    setStep3(false);
  };

  const handleContinueStep4 = () => {
    setStep1(false);
    setStep2(false);
    setStep3(true);
  };

  const handleBack = () => {
    setHideBack(false);
    setStep1(true);
  };

  const handleUpgradeComplete = () => {
    if (pendingAction) {
      pendingAction();
      setPendingAction(null);
    }
  };

  function handleClose() {
    if (isUpgrading) {
      return;
    }
    setOpenModal(false);
    setStep1(true);
    setHideBack(false);
    if (step2 || step3) {
      upgradeFreemiumAccount(selectedPlan, 'start_your_advisorycloud');
      handleUpgradeComplete();
    }
  }

  const sendFSViewedEvent = (upgradeAttempted = false) => {
    sendFSEventUpgradeViewed({
      accountDetails,
      currentPlan: currentPlan,
      asLoggedUserDetails,
      currentUserInfo,
      source: location?.state?.fromBanner ? 'banner' : 'gate',
      type,
      upgradeType: 'freemium',
      upgradeAttempted,
    });
  };

  const handleOptionChange = (plan, option, data) => {
    if (option === 1) {
      setPlanData({ plan: 'mo', option: 1, data: monthlyUpgradePlus });
    } else {
      setPlanData({ plan: 'mo', option: 2, data: monthlyUpgradeManaged });
    }
  };

  useEffect(() => {
    async function fetchData() {
      try {
        const planResponses = await getCompanyRecurlyPlan({
          recurlyPlanCode: accountDetails.recurlyPlanCode,
        });

        const {
          acPlusUpgradeOptionOne,
          acPlusUpgradeOptionTwo,
          freemiumUpgradeOne,
          freemiumUpgradeTwo,
        } = planResponses.data.getRecurlyPlan;
        console.log('acPlusUpgradeOptionTwo', acPlusUpgradeOptionTwo);
        console.log('freemiumUpgradeTwo', freemiumUpgradeTwo);
        if (acPlusUpgradeOptionOne) {
          const planResponsesMonthPlus = await getAdvisorPlanUpgrades({
            RECURLY_PLAN_CODE: acPlusUpgradeOptionOne,
          });
          setMonthlyUpgradePlus(
            planResponsesMonthPlus.data.getFreeTrialUpgrades[0]
          );
        }
        if (acPlusUpgradeOptionTwo) {
          const planResponsesAnnualPlus = await getAdvisorPlanUpgrades({
            RECURLY_PLAN_CODE: acPlusUpgradeOptionTwo,
          });
          setAnnualUpgradePlus(
            planResponsesAnnualPlus.data.getFreeTrialUpgrades[0]
          );
        }
        if (freemiumUpgradeOne) {
          const planResponsesMonthManaged = await getAdvisorPlanUpgrades({
            RECURLY_PLAN_CODE: freemiumUpgradeOne,
          });
          setMonthlyUpgradeManaged(
            planResponsesMonthManaged.data.getFreeTrialUpgrades[0]
          );
        }
        if (freemiumUpgradeTwo) {
          const planResponsesAnnualManaged = await getAdvisorPlanUpgrades({
            RECURLY_PLAN_CODE: freemiumUpgradeTwo,
          });
          setAnnualUpgradeManaged(
            planResponsesAnnualManaged.data.getFreeTrialUpgrades[0]
          );
        }
      } catch (error) {
        console.error('Failed to fetch data:', error);
      }
    }
    eventBus.on(eventBusValues.triggerCompanyUpgradeAction, (data) => {
      if (data && data.planCode === 'managed') {
        setPlanData({ plan: 'mo', option: 2, data: monthlyUpgradeManaged });
        handleContinue();
        setIsFirstLoad(false);
        setHideBack(true);
      }
      if (data && data.planCode === 'plus') {
        setPlanData({ plan: 'mo', option: 1, data: monthlyUpgradePlus });
        handleContinue();
        setIsFirstLoad(false);
        setHideBack(true);
      }
      if (data && data.action) {
        setPendingAction(() => data.action);
      }
      setOpenModal(true);
    });

    const profileParam = new URLSearchParams(location.search).get(
      'b2b_freemium_upgrade_popup'
    );

    if (profileParam) {
      if (profileParam === 'step1') {
        setIsFirstLoad(true);
      } else if (profileParam === 'mab') {
        setPlanData({ plan: 'mo', option: 2, data: monthlyUpgradeManaged });
        setIsFirstLoad(false);
        handleContinue();
        setHideBack(true);
      } else if (profileParam === 'sya') {
        setPlanData({ plan: 'mo', option: 1, data: monthlyUpgradePlus });
        setIsFirstLoad(false);
        handleContinue();
        setHideBack(true);
      }
      setOpenModal(true);
    }

    fetchData();
    return () => {
      eventBus.remove(eventBusValues.triggerCompanyUpgradeAction);
    };
  }, []);

  return (
    <div>
      <Dialog
        open={openModal}
        fullScreen={isSM}
        disableBackdropClick={isUpgrading}
        disableEscapeKeyDown={isUpgrading}
        onClose={() => {
          handleClose();
          sendFSViewedEvent();
        }}
        PaperProps={{
          id: 'companyFreemiumUpgradeModal',
          sx: {
            borderRadius: isSM ? '0px' : '15px',
            boxShadow: '0 5px 20px rgba(0, 0, 0, 0.07)',
            height: { xxs: '100%', sm: '742px' },
            width: { xxs: '100%', sm: '1200px' },
          },
        }}
      >
        {isSM && (
          <div
            style={{
              position: 'absolute',
              top: '20px',
              right: '20px',
              cursor: 'pointer',
            }}
          >
            <Clear
              onClick={() => {
                handleClose();
                sendFSViewedEvent();
              }}
            />
          </div>
        )}

        {isFirstLoad ? (
          <div>
            <FreemiumStep1
              isUserSegment={accountDetails.userSegment}
              onContinue={() => {
                handleContinue();
                setHideBack(false);
                setIsFirstLoad(false);
              }}
              onOptionChange={handleOptionChange}
              setOpenModal={setOpenModal}
            />
          </div>
        ) : (
          <Slide direction="right" in={step1} mountOnEnter unmountOnExit>
            <div>
              <FreemiumStep1
                isUserSegment={accountDetails.userSegment}
                onContinue={handleContinue}
                onOptionChange={handleOptionChange}
                setOpenModal={setOpenModal}
              />
            </div>
          </Slide>
        )}
        <Slide
          direction="left"
          in={!step1 && !step2 && !step3}
          mountOnEnter
          unmountOnExit
          timeout={800}
        >
          <div>
            <FreemiumStep2
              planData={planData}
              goBack={handleBack}
              closeModal={handleClose}
              month1={monthlyUpgradePlus}
              month2={monthlyUpgradeManaged}
              annual1={annualUpgradePlus}
              annual2={annualUpgradeManaged}
              hideBack={hideBack}
              pendingAction={pendingAction}
              handleContinueStep3={handleContinueStep3}
              handleContinueStep4={handleContinueStep4}
              setSelectedPlanFinal={setSelectedPlan}
              isUpgrading={isUpgrading}
              setIsUpgrading={setIsUpgrading}
            />
          </div>
        </Slide>

        <Slide
          direction="left"
          in={step2}
          mountOnEnter
          unmountOnExit
          timeout={800}
        >
          <div style={{ marginTop: '50px' }}>
            <AdvisoryCloudPlusOnboarding />
          </div>
        </Slide>

        <Slide
          direction="left"
          in={step3}
          mountOnEnter
          unmountOnExit
          timeout={800}
        >
          <Box
            sx={{
              my: '200px',
              textAlign: 'center',
              mx: 'auto',
            }}
            variant="contained"
          >
            <Typography
              sx={{
                fontSize: '24px',
                fontWeight: 600,
                color: '#3171F6',
              }}
              gutterBottom
            >
              Success, your advisory board has been upgraded!
            </Typography>
            <List sx={{ textAlign: 'left', mt: 1, mb: 3, fontSize: '18px' }}>
              <ListItem sx={{ display: 'list-item', pl: 2 }}>
                1. You can now invite top advisors to your board
              </ListItem>
              <ListItem sx={{ display: 'list-item', pl: 2 }}>
                2. Make sure your Profile is updated to attract additional
                interest in our Board Directory
              </ListItem>
            </List>

            <Button
              sx={{
                borderWidth: '2px',
                padding: '15px 50px 15px 50px',
                textTransform: 'none',
                height: '45px',
                borderRadius: '40px',
                borderColor: '#3171F6',
              }}
              variant="contained"
              size="large"
              onClick={handleClose}
            >
              Continue
            </Button>
          </Box>
        </Slide>
      </Dialog>
    </div>
  );
};

export default CompanyFreemiumModal;
